import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X, Phone, MessageCircle, Sparkles, Wrench, FileCheck, Camera, Calculator, ClipboardList, Award } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import GoldRule from "@/components/GoldRule";
import ServiceSelectCards from "@/components/ServiceSelectCards";
import TheAscendiaDifference from "@/components/TheAscendiaDifference";
import AscendiaRescue from "@/components/AscendiaRescue";
import TiltCard from "@/components/TiltCard";
import Lightbox from "@/components/Lightbox";
import { Skeleton } from "@/components/ui/skeleton";
import { PrimaryButton, SecondaryButton, GhostButton, Section, SectionHeading } from "@/components/ui-helpers";
import { api, mediaUrl } from "@/lib/api";
import { useSite, whatsappLink } from "@/context/SiteContext";

const PILLARS = [
  { icon: Sparkles, t: "Meticulous Detailing", d: "The details most overlook — tracks, hinges, edges and interiors — done properly." },
  { icon: Wrench, t: "Professional Equipment", d: "Steam and wet/dry vacuum systems with surface-appropriate products." },
  { icon: MessageCircle, t: "Clear & Honest Communication", d: "Transparent written scopes and quotes. No surprises, ever." },
  { icon: FileCheck, t: "Final Quality Inspection", d: "Every job finishes with a documented quality walkthrough." },
];
const CLEANING = ["Bathrooms", "Kitchen", "Floors", "Dusting", "General surfaces", "Vacuuming and mopping", "Move-in preparation"];
const DETAILING = ["Window tracks and frames", "Cabinet and drawer interiors", "Hinges and hidden areas", "Steam cleaning", "Residue treatment", "Baseboards and edges", "Fixtures and crevices", "Final detail inspection"];
const STEPS = [
  { n: "01", t: "Send Property Details", d: "Share your property info — upload photos or a walkthrough video for accurate pricing." },
  { n: "02", t: "Receive a Tailored Quote", d: "We assess the size, condition and scope, then send a clear, tailored quote." },
  { n: "03", t: "Confirm Your Date", d: "Choose a date that suits you and we'll lock it in." },
  { n: "04", t: "We Clean, Detail & Inspect", d: "Our team executes the scope and finishes with a final quality inspection." },
];

const TRUST_STATEMENTS = [
  { icon: FileCheck, t: "Final Quality Inspection", d: "Every job ends with a documented walkthrough." },
  { icon: ClipboardList, t: "Detailed Written Scopes", d: "You always know exactly what's included." },
  { icon: Wrench, t: "Professional Equipment", d: "Steam & surface-appropriate systems." },
  { icon: Award, t: "Owner-Led Standards", d: "Held to a personal standard, every visit." },
];
const TRUST_POINTS = [
  { icon: ClipboardList, t: "Clear Scope" },
  { icon: Wrench, t: "Professional Equipment" },
  { icon: Camera, t: "Real Results" },
  { icon: FileCheck, t: "Final Inspection" },
];

export default function Home() {
  const site = useSite();
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [spot, setSpot] = useState({ x: 72, y: 28, on: false });
  const onHeroMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setSpot({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100, on: true });
  };
  useEffect(() => {
    api.get("/gallery")
      .then(({ data }) => setFeatured(data.slice(0, 3)))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot load on mount
  }, []);

  return (
    <>
      <SEO title="Premium Property Detailing in Israel" description="We don't just clean. We detail. Premium post-construction, move-in, deep-cleaning and property-detailing services throughout Israel." />

      {/* HERO */}
      <section data-testid="home-hero" className="relative overflow-hidden" onMouseMove={onHeroMove} onMouseLeave={() => setSpot((s) => ({ ...s, on: false }))}>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-[#0B0B0C]" />
        <div className="hero-glow absolute inset-0" />
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 transition-opacity duration-300" style={{ opacity: spot.on ? 1 : 0, background: `radial-gradient(600px circle at ${spot.x}% ${spot.y}%, rgba(200,161,74,0.12), transparent 55%)` }} />
        <div className="container-ascendia relative py-16 sm:py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="kicker mb-5">Premium Property Detailing · {site.service_area}</p>
              <h1 className="display-1 text-white">
                We Don't Just Clean.<br />
                <span className="relative inline-block text-gold">
                  We Detail.
                  <span className="absolute -bottom-1 left-0 h-px w-full bg-gradient-to-r from-gold/70 to-transparent" aria-hidden="true" />
                </span>
              </h1>
              <p className="mt-6 lede">
                Premium post-construction, move-in, deep-cleaning and property detailing across Israel. The details most people overlook are the details we focus on.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <PrimaryButton to="/property-intelligence" data-testid="hero-property-intelligence-button">Build My Property Plan <ArrowRight className="h-4 w-4" /></PrimaryButton>
                <SecondaryButton to="/contact" data-testid="home-hero-request-quote-button">Request a Quote</SecondaryButton>
                <GhostButton href={whatsappLink(site)} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-button"><MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp</GhostButton>
              </div>
              <p className="mt-5 text-sm text-muted-c">
                New here?{" "}
                <Link to="/account/signup" data-testid="hero-create-account-link" className="text-gold-soft hover:text-gold font-medium underline decoration-gold/40 underline-offset-4">Create your Ascendia account</Link>
                {" "}to save properties, track quotes & earn rewards.
              </p>
            </div>
            <Reveal delay={0.15}>
              <TiltCard max={7} className="rounded-2xl">
                <div className="overflow-hidden rounded-2xl border" style={{ borderColor: "var(--border-strong)" }}>
                  <img src={mediaUrl("/api/files/ascendia/gallery/sink-faucet.jpeg")} alt="Ascendia before and after: sink and faucet detailing" className="w-full h-auto object-cover" />
                </div>
              </TiltCard>
              <p className="mt-3 text-center text-xs text-muted-c">Real Ascendia project · Sink &amp; faucet detailing</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TRUST STATEMENTS STRIP */}
      <section className="border-y border-[color:var(--border-subtle)] bg-[#0e0e0f] noise-overlay" data-testid="home-trust-strip">
        <div className="container-ascendia py-10 sm:py-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8">
            {TRUST_STATEMENTS.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.08} className="flex items-start gap-3">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gold/10 border border-[color:var(--border-subtle)]"><s.icon className="h-5 w-5 text-gold-soft" strokeWidth={1.5} /></span>
                <div>
                  <p className="font-display text-sm sm:text-base font-semibold text-white leading-tight">{s.t}</p>
                  <p className="mt-1 text-xs text-muted-c u-body">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE SELECTOR */}
      <Section>
        <Reveal><SectionHeading kicker="Where to start" title="What does your property need?" subtitle="Choose the service that fits your property's condition and the finish you're after." /></Reveal>
        <div className="mt-12"><ServiceSelectCards /></div>
      </Section>

      {/* FEATURED TRANSFORMATIONS (moved up — real proof early) */}
      {(loading || featured.length > 0) && (
        <Section className="bg-[#0e0e0f] border-y border-[color:var(--border-subtle)] noise-overlay">
          <div className="flex items-end justify-between gap-4">
            <SectionHeading kicker="Real results · never altered" title="Featured transformations" subtitle="Authentic before-and-after from real Ascendia projects. No retouching, no AI — just the work." />
            <Link to="/gallery" className="hidden sm:inline-flex items-center gap-1 text-sm text-gold-soft hover:text-gold whitespace-nowrap">View gallery <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} data-testid="featured-skeleton">
                  <Skeleton className="w-full aspect-[4/3] rounded-xl bg-white/[0.05]" />
                  <Skeleton className="mt-3 h-4 w-3/4 bg-white/[0.05]" />
                </div>
              ))
            ) : (
              featured.map((g, i) => (
                <Reveal key={g.id} delay={i * 0.1}>
                  <button type="button" onClick={() => setLightboxIdx(i)} className="group block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)] rounded-xl" data-testid={`featured-transformation-${i}`}>
                    <TiltCard max={6} className="rounded-xl">
                      <div className="relative overflow-hidden rounded-xl border bg-black" style={{ borderColor: "var(--border-strong)" }}>
                        <span className="absolute left-3 top-3 z-10 rounded-full border border-[color:var(--border-strong)] bg-black/70 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-gold-soft backdrop-blur">Before / After</span>
                        <img src={mediaUrl(g.image)} alt={g.title} loading="lazy" decoding="async" className="w-full h-auto object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-4 pt-10">
                          <p className="text-sm font-semibold text-white">{g.title}</p>
                          <p className="mt-0.5 text-[11px] uppercase tracking-[0.14em] text-gold-soft">{g.service}</p>
                        </div>
                      </div>
                    </TiltCard>
                  </button>
                </Reveal>
              ))
            )}
          </div>
          {!loading && (
            <div className="mt-12 flex justify-center">
              <SecondaryButton to="/gallery" data-testid="see-all-transformations-button">See All Transformations <ArrowRight className="h-4 w-4" /></SecondaryButton>
            </div>
          )}
          <Lightbox items={featured} index={lightboxIdx} onClose={() => setLightboxIdx(null)} onIndexChange={setLightboxIdx} />
        </Section>
      )}

      {/* THE ASCENDIA DIFFERENCE (merged interactive section) */}
      <Section>
        <Reveal><SectionHeading center kicker="The Ascendia Difference" title="Where cleaning ends, detailing begins." subtitle="Explore the difference area by area — then see the details ordinary cleaning walks past." /></Reveal>
        <div className="mt-12"><TheAscendiaDifference /></div>
      </Section>

      {/* HOW IT WORKS — PROPERTY JOURNEY */}
      <Section className="bg-[#0e0e0f] border-y border-[color:var(--border-subtle)] noise-overlay">
        <Reveal><SectionHeading kicker="Your property journey" title="From property details to move-in ready" subtitle="Upload photos or a walkthrough video and we'll price it accurately — no site visit required to get started." /></Reveal>
        <div className="mt-14 relative">
          {/* connecting rail: horizontal on desktop, vertical on mobile */}
          <div aria-hidden="true" className="hidden lg:block absolute left-0 right-0 top-6 h-px bg-gradient-to-r from-transparent via-[color:var(--border-strong)] to-transparent" />
          <div aria-hidden="true" className="lg:hidden absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[color:var(--border-strong)] to-transparent" />
          <ol className="grid lg:grid-cols-4 gap-8 lg:gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <li className="relative flex lg:flex-col gap-4 lg:gap-0 pl-0">
                  <span className="relative z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border border-[color:var(--border-strong)] bg-[#0B0B0C] font-display text-base font-semibold text-gold">{s.n}</span>
                  <div className="lg:mt-5">
                    <h3 className="font-display text-lg font-semibold text-white u-tracking-tight">{s.t}</h3>
                    <p className="mt-2 text-sm text-secondary u-body max-w-xs">{s.d}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
        <div className="mt-10 flex items-center gap-2 text-sm text-muted-c"><Camera className="h-4 w-4 text-gold-soft" /> Upload photos or a walkthrough video for the most accurate quote.</div>
      </Section>

      {/* WHY ASCENDIA — 4 pillars */}
      <Section>
        <Reveal><SectionHeading center kicker="Why Ascendia" title="Four things we never compromise on" /></Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map((p, i) => (
            <Reveal key={p.t} delay={i * 0.05}>
              <div className="ascendia-card card-accent-top h-full p-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/10 border border-[color:var(--border-subtle)]">
                  <p.icon className="h-5 w-5 text-gold-soft" strokeWidth={1.5} />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-white u-tracking-tight">{p.t}</h3>
                <p className="mt-2 text-sm text-secondary u-body">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* ASCENDIA RESCUE */}
      <Section className="bg-[#0e0e0f] border-y border-[color:var(--border-subtle)] noise-overlay">
        <Reveal><AscendiaRescue /></Reveal>
      </Section>

      {/* ASK ASCENDIA ENTRY */}
      <Section>
        <div className="ascendia-card card-accent-top p-8 sm:p-10">
          <div className="mx-auto max-w-2xl text-center">
            <p className="kicker mb-3">Ask Ascendia</p>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white u-tracking-tight">What's going on with your property?</h2>
            <p className="mt-3 text-sm text-secondary">Premium photo analysis, service matching and honest guidance — in seconds.</p>
            <div className="mt-8 grid gap-3 grid-cols-2 lg:grid-cols-4">
              {[
                { to: "/ask?intent=photo", icon: Camera, label: "Analyze a Photo", sub: "Show us the mess", tid: "ask-entry-photo" },
                { to: "/ask?intent=service", icon: Sparkles, label: "Find My Service", sub: "Match my needs", tid: "ask-entry-service" },
                { to: "/ask?intent=estimate", icon: Calculator, label: "Estimate My Property", sub: "Ballpark pricing", tid: "ask-entry-estimate" },
                { to: "/ask?intent=ask", icon: MessageCircle, label: "Ask Anything", sub: "Get honest advice", tid: "ask-entry-ask" },
              ].map((a) => (
                <Link key={a.tid} to={a.to} data-testid={a.tid} className="group flex flex-col items-center justify-center gap-3 rounded-xl border border-[color:var(--border-neutral)] bg-white/[0.03] p-6 min-h-[9.5rem] transition-[background-color,border-color,transform] duration-150 hover:bg-white/[0.06] hover:border-gold active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold/10 border border-[color:var(--border-subtle)] group-hover:border-[color:var(--border-strong)]"><a.icon className="h-6 w-6 text-gold-soft" strokeWidth={1.5} /></span>
                  <span className="text-sm font-semibold text-white leading-tight">{a.label}</span>
                  <span className="text-[11px] text-muted-c">{a.sub}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section className="bg-[#0e0e0f] border-t border-[color:var(--border-subtle)]">
        <div className="text-center">
          <GoldRule className="max-w-xs mx-auto mb-8" />
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white">Ready to experience the Ascendia standard?</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-3" data-testid="cta-trust-strip">
            {TRUST_POINTS.map((p) => (
              <span key={p.t} className="inline-flex items-center gap-2 text-sm text-secondary"><p.icon className="h-4 w-4 text-gold-soft" strokeWidth={1.6} /> {p.t}</span>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <PrimaryButton to="/contact" data-testid="final-cta-quote-button">Get My Quote <ArrowRight className="h-4 w-4" /></PrimaryButton>
            <GhostButton href={whatsappLink(site)} target="_blank" rel="noopener noreferrer"><MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp Us</GhostButton>
            <GhostButton href={`tel:${site.phone}`}><Phone className="h-4 w-4 text-gold-soft" /> Call {site.phone}</GhostButton>
          </div>
        </div>
      </Section>
    </>
  );
}
